import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { History, ChevronDown, ChevronUp } from "lucide-react";

export const ACTION_LABELS = {
  check_in: "Checked in",
  delete_visit: "Deleted visit",
  create: "Created client",
  update: "Updated info",
  delete: "Deleted",
  merge_client: "Merged clients",
  clear_duplicate_flag: "Cleared duplicate flag",
  update_coat_settings: "Updated coat settings",
  record_coat: "Recorded coat distribution",
  delete_coat: "Removed coat record",
  create_staff: "Created staff account",
  update_staff: "Updated staff account",
  delete_staff: "Deleted staff account",
  send_staff_invite: "Sent staff invite",
  update_page_permissions: "Updated page permissions",
  approve_update: "Approved client update",
  reject_update: "Rejected client update",
  add_member: "Added household member",
  update_member: "Updated household member",
  delete_member: "Removed household member",
  create_via_sharing_tree: "Created client via Sharing Tree",
  soft_delete_household: "Removed Sharing Tree household",
  restore_household: "Restored Sharing Tree household",
  permanent_delete_household: "Permanently deleted Sharing Tree household",
  reset_household_reminder: "Reset pickup reminder",
  send_pickup_reminders: "Sent pickup reminders",
  send_single_reminder: "Sent individual pickup reminder",
  update_period: "Updated Sharing Tree period",
  import_csv: "Imported clients from CSV",
  update_checkin_settings: "Updated check-in settings",
  staff_add_to_queue: "Added client to queue",
};

export function actionLabel(action) {
  return ACTION_LABELS[action] || action;
}

function actionDetail(log) {
  if (log.action !== "delete_visit" || !log.details?.visit_date) return "";
  const date = new Date(`${String(log.details.visit_date).slice(0, 10)}T00:00:00`);
  const dateLabel = Number.isNaN(date.getTime()) ? log.details.visit_date : date.toLocaleDateString();
  const weight = Number(log.details.total_weight_lbs) > 0
    ? ` · ${log.details.total_weight_lbs} lbs`
    : "";
  return `${dateLabel}${weight}`;
}

function timeAgo(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

async function fetchQuery({ targetType, targetId, limit }) {
  const params = new URLSearchParams({ limit });
  if (targetType) params.set("target_type", targetType);
  if (targetId) params.set("target_id", targetId);
  const { data } = await api.get(`/audit-logs?${params}`);
  return data;
}

/**
 * ActivitySection — collapsible activity feed for any object or group of objects.
 *
 * Usage (simple):
 *   <ActivitySection targetType="client" targetId={id} title="Client Activity" />
 *
 * Usage (multi-query — fetches multiple target types/ids and merges):
 *   <ActivitySection
 *     targetType="coat_settings,coat_distribution"  // comma-separated → single $in call
 *     title="Coat Changes"
 *   />
 *   <ActivitySection
 *     targetType="client"
 *     targetId={clientId}
 *     extraQueries={[{ targetType: "member", targetId: memberIds }]}
 *     title="Client Activity"
 *   />
 */
export default function ActivitySection({
  targetType,
  targetId,
  extraQueries,
  limit = 20,
  title = "Activity",
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);

  const queryKey = JSON.stringify({ targetType, targetId, extraQueries });
  const queryKeyRef = useRef(queryKey);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const queries = [{ targetType, targetId }, ...(extraQueries || [])];
      const results = await Promise.all(
        queries.map((q) => fetchQuery({ targetType: q.targetType, targetId: q.targetId, limit }))
      );
      const merged = results.flat().sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      const seen = new Set();
      const deduped = merged.filter((l) => {
        if (seen.has(l.id)) return false;
        seen.add(l.id);
        return true;
      });
      setLogs(deduped.slice(0, limit));
      setFetched(true);
    } catch {
      setFetched(true);
    } finally {
      setLoading(false);
    }
  }, [queryKey, limit]);

  useEffect(() => {
    if (open) {
      const changed = queryKey !== queryKeyRef.current;
      queryKeyRef.current = queryKey;
      if (changed || !fetched) fetchLogs();
    }
  }, [open, queryKey, fetched, fetchLogs]);

  function toggle() {
    setOpen((v) => !v);
  }

  return (
    <div className="fb-card overflow-hidden" data-testid="activity-section">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <History className="w-4 h-4" style={{ color: "var(--fb-text-muted)" }} />
          <span className="font-semibold text-sm">{title}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4" style={{ color: "var(--fb-text-muted)" }} />
          : <ChevronDown className="w-4 h-4" style={{ color: "var(--fb-text-muted)" }} />}
      </button>

      {open && (
        <div className="border-t" style={{ borderColor: "var(--fb-border)" }}>
          {loading && (
            <div className="px-5 py-4 text-xs text-center" style={{ color: "var(--fb-text-muted)" }}>
              Loading…
            </div>
          )}
          {!loading && fetched && logs.length === 0 && (
            <div className="px-5 py-4 text-xs text-center" style={{ color: "var(--fb-text-muted)" }}>
              No activity recorded.
            </div>
          )}
          {!loading && logs.map((l, i) => (
            <div
              key={l.id}
              className="flex items-center gap-3 px-5 py-2.5 border-t text-sm"
              style={{ borderColor: i === 0 ? "transparent" : "var(--fb-border)" }}
            >
              <div className="flex-1 min-w-0">
                <span className="font-medium">{l.actor}</span>
                <span className="mx-1.5 text-xs" style={{ color: "var(--fb-text-muted)" }}>·</span>
                <span>{actionLabel(l.action)}</span>
                {actionDetail(l) && (
                  <div className="text-xs mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
                    {actionDetail(l)}
                  </div>
                )}
              </div>
              <div
                className="text-xs shrink-0 tabular-nums"
                style={{ color: "var(--fb-text-muted)" }}
                title={new Date(l.created_at).toLocaleString()}
              >
                {timeAgo(l.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
